import { ResponseInterfacePageable } from '../http/response.interface';

// Essa interface é usada para receber os dados que retornam do upload
export interface ExtractedReportDataModel {
  royaltyRunId: number;
  name: string;
  trackSize: number;
  releaseSize: number;
  totalValue: number;
  totalPayorValue: number;
  totalPayeeValue: number;
  reportMonth: number;
  reportYear: number;
  reportType: string;
  originColumns?: OriginColumn;
  notIdentifiedColumns?: any;
  currencyType: string;
  splits: Split[];
  file: string;
}

export interface OriginColumn {
  columnName: string;
  columnIndex: string;
}

export interface Split {
  splitId: number;
  generatedRoyalties: number;
  amountDueToPayees: number;
  payorAmount: number;
  payorName: string;
  splitName: string;
  assets: Asset[];
  payees: PayeeRoyaltyRun[];
  priorityPayees: PriorityPayee[];
  splitFullDetails: SplitFullDetails;
}

export interface SplitFullDetails {
  id: number;
  createdAt: string;
  updatedAt: string;
  active: boolean;
  name: string;
  payorWallet: PayorWalletRoyaltyRun;
  revenueType: string;
  startDate: string;
  expirationDate: string;
  type: string;
  assets: SplitAssetModel[];
  rules: Rule[];
  payees: PayeeFull[];
  teamSplit: TeamSplit;
  version: number;
  code: string;
  status: string;
}

export interface TeamSplit {
  id: number;
  splitId: number;
  team: Team;
}

export interface PayeeFull {
  id: number;
  createdAt: string;
  updatedAt?: string;
  active: boolean;
  team: Team;
  splitId: number;
  artistType: ArtistPayeeType;
  sharePercent: number;
  languageId: number;
  artist: ArtistPayeeFull;
  dueValue?: number;
  priorityPayee: boolean;
  priorityOrder?: number;
  hasAcceptedTerms: boolean;
  status: string;
  currencyType: string;
}

export interface ArtistPayeeFull {
  id: number;
  name: string;
  email: string;
  fullName: string;
  photo: string;
  background?: string;
}

export interface ArtistPayeeType {
  id: number;
  name: string;
  priority: number;
  roleId: number;
  description?: string;
  nameInPortugueseBr: string;
}

export interface Team {
  id: number;
  name: string;
  hash: string;
  code: string;
}

export interface Rule {
  id: number;
  include: boolean;
  country?: CountryRule;
  dsp?: Dsp;
  splitId: number;
}
export interface Dsp {
  id: number;
  active: boolean;
  name: string;
  distributorStoreId: number;
}

export interface CountryRule {
  id: number;
  name: string;
  nameInPortugueseBr: string;
  flagUrl: string;
  isoAlphaTwo: string;
  isoAlphaThree: string;
  isoCode: string;
  currencyType: string;
}

export interface SplitAssetModel {
  id: number;
  splitId: number;
  release?: ReleaseSplit;
  track?: TrackSplitModel;
}

export interface TrackSplitModel {
  id: number;
  active: boolean;
  externalFileHash?: any;
  hash: string;
  title: string;
  titleVersion?: any;
  trackExternalId?: any;
  primaryGenreId: number;
  secondaryGenreId: number;
  isrc: string;
  lyricsLanguageId?: any;
  lyrics?: any;
  trackType: string;
  teamId: number;
  label: Label;
  artists: ArtistPayeeFull[];
}

export interface ReleaseSplit {
  id: number;
  hash: string;
  title: string;
  titleVersion?: any;
  releaseExternalId?: any;
  primaryGenreId: number;
  secondaryGenreId?: any;
  isTemp: boolean;
  labelId: number;
  teamId: number;
  hasReleased: boolean;
  upc?: any;
  releaseDate?: any;
  releaseTypeEnum: string;
  status: string;
  label: Label;
  tracks: Track[];
  artists: ArtistPayeeFull[];
}

export interface Track {
  id: number;
  createdAt: string;
  updatedAt?: any;
  active: boolean;
  track: TrackDetailItem;
  releaseId: number;
}

export interface TrackDetailItem {
  id: number;
  active: boolean;
  externalFileHash: string;
  hash?: any;
  title: string;
  titleVersion: string;
  trackExternalId?: any;
  primaryGenreId: number;
  secondaryGenreId: number;
  isrc: string;
  lyricsLanguageId?: any;
  lyrics?: any;
  trackType: string;
  teamId: number;
  label: Label;
  artists: ArtistPayeeFull[];
}

export interface Label {
  id: number;
  imageId?: any;
  name: string;
  description: string;
}

export interface PayorWalletRoyaltyRun {
  id: number;
  user: UserPayorWallet;
  chainWalletId: string;
}

export interface UserPayorWallet {
  username: string;
  firstName: string;
  lastName: string;
  profilePhoto: string;
  backGroundPhoto?: any;
  cognitoUsername: string;
}

export interface PriorityPayee {
  appliedAmount: number;
  payeeId: number;
  name: string;
  email: string;
  role: string;
  dueValue: number;
}

export interface PayeeRoyaltyRun {
  royaltiesDue: number;
  payeeId: number;
  name: string;
  email: string;
  role: string;
  percentage: number;
}

export interface Asset {
  track: AssetItem;
  release?: AssetItem;
}

export interface AssetItem {
  generatedRoyalties: number;
  name: string;
  id: number;
  artists: string;
}

export type RoyaltySource =
  | 'DIGITAL_DISTRIBUTOR'
  | 'ASSOCIATION_PUBLIC_PERFORMANCE'
  | 'ASSOCIATION_DIGITAL';

export interface RevenueTypeItem {
  code: string;
  name: string;
}

export interface ReportTypeItem {
  code: string;
  name: string;
  needAskCurrency: boolean;
  imgHash: string;
}

// Essa interface é para trazer os dados de uma royalty já criada
export interface RoyaltyDto {
  id: number;
  name: string;
  trackSize: number;
  totalValue: number;
  totalPayorValue: number;
  totalPayeeValue: number;
  reportMonth: number;
  reportYear: number;
  reportType: ReportType;
  currencyType: CurrencyType;
  royaltySplits: RoyaltyRunSplit[];
  status: StatusRoyaltyType;
  executionDate: string;
  sourceOfFund: string;
}

export interface RoyaltyRunSplit {
  id: number;
  split: {
    id: number;
    name: string;
    payorWallet: {
      user: {
        id: number;
        firstName: string;
        lastName: string;
        profilePhoto: string;
        backGroundPhoto: string;
      };
    };
    revenueType: 'DIGITAL_STREAMING_REVENUE';
    startDate: string;
    expirationDate: string;
    type: RoyaltyType;
    assets: {
      release?: {
        id: number;
        hash: string;
        title: string;
        titleVersion: string;
        hasReleased: boolean;
        upc: string;
        releaseDate: string;
        releaseTypeEnum: string;
        label: {
          imageId: string;
          name: string;
          description: string;
        };
        tracks: {
          track: {
            id: number;
            hash: string;
            title: string;
            titleVersion: string;
            isrc: string;
            label: {
              imageId: string;
              name: string;
              description: string;
            };
            artists: {
              id: number;
              name: string;
              email: string;
              fullName: string;
              photo: string;
              background: string;
              user: string;
            }[];
            artistNames: string;
          };
        }[];
        artists: {
          id: number;
          name: string;
          email: string;
          fullName: string;
          photo: string;
          background: string;
          user: string;
        }[];
        artistNames: string;
      };
      track?: {
        id: number;
        hash: string;
        title: string;
        titleVersion: string;
        isrc: string;
        label: {
          imageId: string;
          name: string;
          description: string;
        };
        artists: {
          id: number;
          name: string;
          email: string;
          fullName: string;
          photo: string;
          background: string;
          user: string;
        }[];
        artistNames: string;
      };
    }[];
    rules: {
      include: boolean;
      country: {
        id: number;
        name: string;
        nameInPortugueseBr: string;
        flagUrl: string;
        isoAlphaTwo: string;
        isoAlphaThree: string;
        isoCode: string;
        currencyType: CurrencyType;
      };
      dsp: {
        id: number;
        name: string;
        distributorStoreId: number;
      };
    }[];
    payees: RoyaltyPayee[];
    teamSplit: {
      team: {
        name: string;
      };
    };
    version: number;
    code: string;
    status: RoyaltyPayeeStatusType;
  };
  generatedAmount: number;
  amountDueToPayees: number;
  payorAmount: number;
  approved: boolean;
  royaltySplitPayees: {
    id: number;
    splitPayee: {
      team: {
        id: number;
        name: string;
        registryType: TeamRegistryType;
      };
      artistType: {
        id: number;
        active: boolean;
        name: string;
        priority: number;
        roleId: number;
        description: string;
        nameInPortugueseBr: string;
      };
      sharePercent: number;
      languageId: number;
      artist: {
        id: number;
        name: string;
        email: string;
        bio?: string;
        uuidViberate?: string;
        artistAppleId?: string;
        artistSpotifyId?: string;
      };
      dueValue: number;
      isPriorityPayee: boolean;
      priorityOrder: number;
      hasAcceptedTerms: boolean;
      status: SplitPayeeStatusType;
    };
    generatedAmount: number;
  }[];
  royaltySplitsAssets: {
    id: number;
    release: string;
    track: {
      id: number;
      externalFileHash: string;
      hash: string;
      title: string;
      titleVersion: string;
      trackExternalId: string;
      primaryGenreId: number;
      secondaryGenreId: number;
      isrc: string;
      explicit: boolean;
      lyricsLanguageId: string;
      lyrics: string;
      trackType: string;
      syncAI: boolean;
      instrumental: boolean;
      startPreview: number;
      duration: number;
      waveFilePath: string;
      teamId: number;
      artists: {
        id: number;
        artistTypeId: number;
        mainArtist: boolean;
        artist: {
          id: number;
          name: string;
          email: string;
          bio?: string;
          uuidViberate?: string;
          artistAppleId?: string;
          artistSpotifyId?: string;
        };
      }[];
      writers: {
        id: number;
        artist: {
          id: number;
          name: string;
          email: string;
          bio?: string;
          uuidViberate?: string;
          artistAppleId?: string;
          artistSpotifyId?: string;
        };
      }[];
    };
    generatedAmount: number;
  }[];
  selected: boolean;
  hover: boolean;
}

export interface RoyaltyPayee {
  id: number;
  team: {
    name: string;
  };
  artistType: {
    name: string;
    priority: number;
    roleId: number;
    description: string;
    nameInPortugueseBr: string;
  };
  sharePercent: number;
  languageId: number;
  artist: {
    id: number;
    name: string;
    email: string;
    bio?: string;
    uuidViberate?: string;
    artistAppleId?: string;
    artistSpotifyId?: string;
  };
  dueValue: number;
  priorityOrder: number;
  hasAcceptedTerms: boolean;
  status: RoyaltyStatusType;
  isPriorityPayee: boolean;
}

export interface RoyaltyRunListData extends ResponseInterfacePageable {
  data: {
    content: RoyaltyDto[];
    size: number;
    totalElements: number;
  };
}

export interface GetRoyaltyRunFindParams {
  search?: string;
  currency?: CurrencyEnum;
  status?: RoyaltyStatusType;
  name?: string;
  month?: number;
  year?: number;
  createdAtStart?: string;
  createdAtEnd?: string;
  reportType?: ReportType;
  page?: number;
  size?: number;
  sort?: string;
}

export interface RoyaltyRunApproveData {
  royaltySplitIds: number[];
  royaltyId: number;
  executionDate: string;
  sourceOfFund: 'ACCOUNT_BALANCE';
}

export type TeamRegistryType = 'ARTIST' | 'LABEL' | 'PARTNER';
export type SplitStatusType = 'PENDING' | 'COMPLETED';
export type SplitPayeeStatusType = 'PENDING' | 'COMPLETED';

export enum FileOriginTypeEnum {
  BELIVE = 'BELIVE',
  ONE_RPM = 'ONE_RPM',
  VERSE_ONE = 'VERSE_ONE',
  REVELATOR = 'REVELATOR',
  IMUSICIAN = 'IMUSICIAN',
}

export type ReportType =
  | 'BELIVE'
  | 'ONE_RPM'
  | 'VERSE_ONE'
  | 'REVELATOR'
  | 'IMUSICIAN';

export enum CurrencyEnum {
  AED = 'AED',
  AFN = 'AFN',
  ALL = 'ALL',
  AMD = 'AMD',
  ANG = 'ANG',
  AOA = 'AOA',
  ARS = 'ARS',
  AUD = 'AUD',
  AWG = 'AWG',
  AZN = 'AZN',
  BAM = 'BAM',
  BBD = 'BBD',
  BDT = 'BDT',
  BGN = 'BGN',
  BHD = 'BHD',
  BIF = 'BIF',
  BMD = 'BMD',
  BND = 'BND',
  BOB = 'BOB',
  BRL = 'BRL',
  BSD = 'BSD',
  BTN = 'BTN',
  BWP = 'BWP',
  BYN = 'BYN',
  BZD = 'BZD',
  CAD = 'CAD',
  CDF = 'CDF',
  CHF = 'CHF',
  CLP = 'CLP',
  CNY = 'CNY',
  COP = 'COP',
  CRC = 'CRC',
  CUP = 'CUP',
  CVE = 'CVE',
  CZK = 'CZK',
  DJF = 'DJF',
  DKK = 'DKK',
  DOP = 'DOP',
  DZD = 'DZD',
  EGP = 'EGP',
  ERN = 'ERN',
  ETB = 'ETB',
  EUR = 'EUR',
  FJD = 'FJD',
  FKP = 'FKP',
  GBP = 'GBP',
  GEL = 'GEL',
  GHS = 'GHS',
  GIP = 'GIP',
  GMD = 'GMD',
  GNF = 'GNF',
  GTQ = 'GTQ',
  GYD = 'GYD',
  HKD = 'HKD',
  HNL = 'HNL',
  HTG = 'HTG',
  HUF = 'HUF',
  IDR = 'IDR',
  ILS = 'ILS',
  INR = 'INR',
  IQD = 'IQD',
  IRR = 'IRR',
  ISK = 'ISK',
  JMD = 'JMD',
  JOD = 'JOD',
  JPY = 'JPY',
  KES = 'KES',
  KGS = 'KGS',
  KHR = 'KHR',
  KMF = 'KMF',
  KPW = 'KPW',
  KRW = 'KRW',
  KWD = 'KWD',
  KYD = 'KYD',
  KZT = 'KZT',
  LAK = 'LAK',
  LBP = 'LBP',
  LKR = 'LKR',
  LRD = 'LRD',
  LSL = 'LSL',
  LYD = 'LYD',
  MAD = 'MAD',
  MDL = 'MDL',
  MGA = 'MGA',
  MKD = 'MKD',
  MMK = 'MMK',
  MNT = 'MNT',
  MOP = 'MOP',
  MRU = 'MRU',
  MUR = 'MUR',
  MVR = 'MVR',
  MWK = 'MWK',
  MXN = 'MXN',
  MYR = 'MYR',
  MZN = 'MZN',
  NAD = 'NAD',
  NGN = 'NGN',
  NIO = 'NIO',
  NOK = 'NOK',
  NPR = 'NPR',
  NZD = 'NZD',
  OMR = 'OMR',
  PAB = 'PAB',
  PEN = 'PEN',
  PGK = 'PGK',
  PHP = 'PHP',
  PKR = 'PKR',
  PLN = 'PLN',
  PYG = 'PYG',
  QAR = 'QAR',
  RON = 'RON',
  RSD = 'RSD',
  RUB = 'RUB',
  RWF = 'RWF',
  SAR = 'SAR',
  SBD = 'SBD',
  SCR = 'SCR',
  SDG = 'SDG',
  SEK = 'SEK',
  SGD = 'SGD',
  SHP = 'SHP',
  SLL = 'SLL',
  SOS = 'SOS',
  SRD = 'SRD',
  SSP = 'SSP',
  STN = 'STN',
  SYP = 'SYP',
  SZL = 'SZL',
  THB = 'THB',
  TJS = 'TJS',
  TMT = 'TMT',
  TND = 'TND',
  TOP = 'TOP',
  TRY = 'TRY',
  TTD = 'TTD',
  TWD = 'TWD',
  TZS = 'TZS',
  UAH = 'UAH',
  UGX = 'UGX',
  USD = 'USD',
  UYU = 'UYU',
  UZS = 'UZS',
  VES = 'VES',
  VND = 'VND',
  VUV = 'VUV',
  WST = 'WST',
  XAF = 'XAF',
  XCD = 'XCD',
  XOF = 'XOF',
  XPF = 'XPF',
  YER = 'YER',
  ZAR = 'ZAR',
  ZMW = 'ZMW',
  ZWL = 'ZWL',
}

export type CurrencyType =
  | 'AED'
  | 'AFN'
  | 'ALL'
  | 'AMD'
  | 'ANG'
  | 'AOA'
  | 'ARS'
  | 'AUD'
  | 'AWG'
  | 'AZN'
  | 'BAM'
  | 'BBD'
  | 'BDT'
  | 'BGN'
  | 'BHD'
  | 'BIF'
  | 'BMD'
  | 'BND'
  | 'BOB'
  | 'BRL'
  | 'BSD'
  | 'BTN'
  | 'BWP'
  | 'BYN'
  | 'BZD'
  | 'CAD'
  | 'CDF'
  | 'CHF'
  | 'CLP'
  | 'CNY'
  | 'COP'
  | 'CRC'
  | 'CUP'
  | 'CVE'
  | 'CZK'
  | 'DJF'
  | 'DKK'
  | 'DOP'
  | 'DZD'
  | 'EGP'
  | 'ERN'
  | 'ETB'
  | 'EUR'
  | 'FJD'
  | 'FKP'
  | 'GBP'
  | 'GEL'
  | 'GHS'
  | 'GIP'
  | 'GMD'
  | 'GNF'
  | 'GTQ'
  | 'GYD'
  | 'HKD'
  | 'HNL'
  | 'HTG'
  | 'HUF'
  | 'IDR'
  | 'ILS'
  | 'INR'
  | 'IQD'
  | 'IRR'
  | 'ISK'
  | 'JMD'
  | 'JOD'
  | 'JPY'
  | 'KES'
  | 'KGS'
  | 'KHR'
  | 'KMF'
  | 'KPW'
  | 'KRW'
  | 'KWD'
  | 'KYD'
  | 'KZT'
  | 'LAK'
  | 'LBP'
  | 'LKR'
  | 'LRD'
  | 'LSL'
  | 'LYD'
  | 'MAD'
  | 'MDL'
  | 'MGA'
  | 'MKD'
  | 'MMK'
  | 'MNT'
  | 'MOP'
  | 'MRU'
  | 'MUR'
  | 'MVR'
  | 'MWK'
  | 'MXN'
  | 'MYR'
  | 'MZN'
  | 'NAD'
  | 'NGN'
  | 'NIO'
  | 'NOK'
  | 'NPR'
  | 'NZD'
  | 'OMR'
  | 'PAB'
  | 'PEN'
  | 'PGK'
  | 'PHP'
  | 'PKR'
  | 'PLN'
  | 'PYG'
  | 'QAR'
  | 'RON'
  | 'RSD'
  | 'RUB'
  | 'RWF'
  | 'SAR'
  | 'SBD'
  | 'SCR'
  | 'SDG'
  | 'SEK'
  | 'SGD'
  | 'SHP'
  | 'SLL'
  | 'SOS'
  | 'SRD'
  | 'SSP'
  | 'STN'
  | 'SYP'
  | 'SZL'
  | 'THB'
  | 'TJS'
  | 'TMT'
  | 'TND'
  | 'TOP'
  | 'TRY'
  | 'TTD'
  | 'TWD'
  | 'TZS'
  | 'UAH'
  | 'UGX'
  | 'USD'
  | 'UYU'
  | 'UZS'
  | 'VES'
  | 'VND'
  | 'VUV'
  | 'WST'
  | 'XAF'
  | 'XCD'
  | 'XOF'
  | 'XPF'
  | 'YER'
  | 'ZAR'
  | 'ZMW'
  | 'ZWL';

export type StatusRoyaltyType =
  | 'SCRATCH'
  | 'CONCLUDED'
  | 'FAILED'
  | 'SCHEDULED'
  | 'WAITING_FOR_PAYMENT';

export type RoyaltyType = 'RELEASE' | ' TRACK' | 'GROUP';
export type RoyaltyStatusType = 'PENDING' | 'COMPLETED';
export type RoyaltyPayeeStatusType =
  | 'PENDING'
  | 'ACTIVE'
  | 'ACTION_REQUIRED'
  | 'INACTIVE';
